import {
  Badge,
  Button,
  Card,
  Code,
  Group,
  SimpleGrid,
  Stack,
  Table,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconCloud, IconRefresh } from "@/lib/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { EmptyState } from "@/components/EmptyState";
import { PageHeader } from "@/components/PageHeader";
import {
  forceSync,
  getSyncStatus,
  listConflicts,
  resolveConflict,
} from "@/lib/api/sync";
import { formatDate, formatDateTime } from "@/lib/format";
import { useAuthStore } from "@/stores/auth";

export function SyncPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canForce = useAuthStore((s) => s.hasPermission("sync.force"));

  const { data: status } = useQuery({
    queryKey: ["sync-status"],
    queryFn: getSyncStatus,
    refetchInterval: 30_000,
  });

  const { data: conflicts } = useQuery({
    queryKey: ["sync-conflicts", "open"],
    queryFn: () => listConflicts("open"),
  });

  const forceMutation = useMutation({
    mutationFn: forceSync,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sync-status"] });
      notifications.show({ color: "blue", message: t("sync.forced") });
    },
  });

  const resolveMutation = useMutation({
    mutationFn: ({ id, resolution }: { id: string; resolution: "keep_local" | "keep_remote" }) =>
      resolveConflict(id, resolution),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sync-conflicts"] });
      notifications.show({ color: "blue", message: t("sync.resolved") });
    },
  });

  return (
    <Stack gap="lg">
      <PageHeader
        title={t("sync.title")}
        back
        actions={
          canForce && (
            <Button
              leftSection={<IconRefresh size={16} strokeWidth={2.2} />}
              radius="xl"
              onClick={() => forceMutation.mutate()}
              loading={forceMutation.isPending}
            >
              {t("sync.force")}
            </Button>
          )
        }
      />

      <Card padding="xl" radius="xl">
        <Text fw={700} size="md" mb="lg">
          {t("sync.status")}
        </Text>
        <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }}>
          <Stat
            label={t("sync.fields.state")}
            value={
              status?.connected ? (
                <Badge color="blue" variant="light" leftSection={<IconCloud size={12} strokeWidth={2.2} />}>
                  {t("sync.connected")}
                </Badge>
              ) : (
                <Badge color="gray" variant="light">
                  {t("sync.offline")}
                </Badge>
              )
            }
          />
          <Stat
            label={t("sync.fields.schema")}
            value={<Code style={{ fontSize: 13 }}>{status?.schema_version}</Code>}
          />
          <Stat
            label={t("sync.fields.last_push")}
            value={status?.last_push_at ? formatDateTime(status.last_push_at) : "—"}
          />
          <Stat label={t("sync.fields.outbox")} value={String(status?.outbox_count ?? 0)} />
        </SimpleGrid>
      </Card>

      <Card padding={0} radius="xl">
        <Text fw={700} size="md" p="lg" pb={0}>
          {t("sync.conflicts")}
        </Text>
        <Table verticalSpacing="md" striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("sync.fields.detected_at")}</Table.Th>
              <Table.Th>{t("sync.fields.entity")}</Table.Th>
              <Table.Th>{t("sync.fields.tier")}</Table.Th>
              <Table.Th></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {conflicts?.items.length === 0 && (
              <Table.Tr>
                <Table.Td colSpan={4}>
                  <EmptyState
                    icon={<IconCloud size={32} strokeWidth={1.5} />}
                    title={t("sync.no_conflicts")}
                  />
                </Table.Td>
              </Table.Tr>
            )}
            {conflicts?.items.map((c) => (
              <Table.Tr key={c.id}>
                <Table.Td>
                  <Text size="sm">{formatDateTime(c.detected_at)}</Text>
                </Table.Td>
                <Table.Td>
                  <Code style={{ fontSize: 12 }}>{c.entity_type}</Code>
                </Table.Td>
                <Table.Td>
                  <Badge
                    variant="light"
                    radius="sm"
                    color={c.tier === "T2" ? "red" : c.tier === "T1" ? "orange" : "gray"}
                  >
                    {c.tier}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  {canForce && (
                    <Group gap="xs">
                      <Button
                        size="xs"
                        variant="light"
                        radius="xl"
                        onClick={() =>
                          resolveMutation.mutate({ id: c.id, resolution: "keep_local" })
                        }
                      >
                        {t("sync.keep_local")}
                      </Button>
                      <Button
                        size="xs"
                        variant="light"
                        color="orange"
                        radius="xl"
                        onClick={() =>
                          resolveMutation.mutate({ id: c.id, resolution: "keep_remote" })
                        }
                      >
                        {t("sync.keep_remote")}
                      </Button>
                    </Group>
                  )}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>
    </Stack>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Stack gap={4}>
      <Text c="dimmed" size="xs" fw={600} tt="uppercase" style={{ letterSpacing: "0.06em" }}>
        {label}
      </Text>
      <Text fw={600} size="sm">
        {value}
      </Text>
    </Stack>
  );
}
